import { R } from '@core/utils/r';
import React, { Fragment, memo, ReactElement, ReactNode, useMemo, useEffect } from 'react';
import { UncontrolledTooltip, UncontrolledTooltipProps, Tooltip } from 'reactstrap';
import { Observable } from 'rxjs';
import { LoadingIndicator } from '@core/components/LoadingIndicator';
import { tap } from 'rxjs/operators';
import { HTMLService } from '@business/services';

export interface WithTooltipProps extends Omit<UncontrolledTooltipProps, 'target'> {
  tooltip: ReactNode;
  key?: string;
  children: ReactElement;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  fetch?(): Observable<string | ReactNode>;
  className?: string;
}

export const WithTooltip = memo((props: WithTooltipProps) => {
  const { tooltip, key, children, placement = 'top', ...rest } = props;
  const [preloadTooltip, setPreloadTooltip] = React.useState<string | ReactNode | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const inKey = useMemo(() => {
    return key || `tooltip-control-${R.random(1, 99999)}`;
  }, [key]);

  const inChildren = useMemo(() => {
    const inputReactObject = React.Children.only(children);
    const clonedChild = React.cloneElement(inputReactObject, {
      id: inKey,
    });

    return clonedChild;
  }, [children, inKey]);

  useEffect(() => {
    if (props.fetch && isOpen && preloadTooltip == null) {
      props
        .fetch()
        .pipe(
          tap(data => {
            if (typeof data === 'string') setPreloadTooltip(HTMLService.toReact(data) as any);
            else setPreloadTooltip(data);
          }),
        )
        .subscribe();
    }
  }, [isOpen, preloadTooltip, props]);

  if (props.fetch) {
    return (
      <Fragment>
        {inChildren}
        {!R.isEmpty(props.tooltip) && (
          <Tooltip isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} target={inKey} placement={placement} {...rest}>
            {!preloadTooltip && <LoadingIndicator />}
            {preloadTooltip}
          </Tooltip>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {inChildren}
      {!R.isEmpty(props.tooltip) && (
        <UncontrolledTooltip target={inKey} placement={placement} {...rest}>
          {props.tooltip}
        </UncontrolledTooltip>
      )}
    </Fragment>
  );
});
